import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, ShieldCheck, KeyRound, Smartphone, CheckCircle2, SkipForward } from "lucide-react";
import { withCsrfHeader, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

type Step = "password" | "mfa";

function PasswordStrengthBar({ password }: { password: string }) {
  const checks = [
    password.length >= 8,
    /[A-Z]/.test(password),
    /[0-9]/.test(password),
    /[^A-Za-z0-9]/.test(password),
  ];
  const score = checks.filter(Boolean).length;
  const labels = ["", "Weak", "Fair", "Good", "Strong"];
  const colors = ["", "bg-red-500", "bg-orange-400", "bg-yellow-400", "bg-green-500"];

  if (!password) return null;

  return (
    <div className="mt-2 space-y-1">
      <div className="flex gap-1 h-1.5">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`flex-1 rounded-full transition-colors ${i <= score ? colors[score] : "bg-muted"}`}
          />
        ))}
      </div>
      {score > 0 && (
        <p className="text-xs text-muted-foreground">{labels[score]}</p>
      )}
    </div>
  );
}

export default function FirstLogin() {
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const [step, setStep] = useState<Step>("password");
  const [checking, setChecking] = useState(true);

  // Password step state
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [submittingPassword, setSubmittingPassword] = useState(false);

  // MFA step state
  const [totpQr, setTotpQr] = useState<string | null>(null);
  const [totpSecret, setTotpSecret] = useState<string | null>(null);
  const [totpToken, setTotpToken] = useState("");
  const [startingTotp, setStartingTotp] = useState(false);
  const [verifyingTotp, setVerifyingTotp] = useState(false);
  const [totpError, setTotpError] = useState("");

  // Post-MFA redirect state
  const [workspaceSlug, setWorkspaceSlug] = useState<string | null>(null);

  // Guard: if the session does not have mustResetPassword, redirect away.
  useEffect(() => {
    fetch("/api/auth/first-login-status", { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (!data.mustResetPassword) {
          navigate("/");
        } else {
          setChecking(false);
        }
      })
      .catch(() => {
        navigate("/");
      });
  }, [navigate]);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError("");

    if (newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }

    setSubmittingPassword(true);
    try {
      const res = await fetch("/api/auth/complete-first-login", {
        method: "POST",
        headers: await withCsrfHeader({ "Content-Type": "application/json" }),
        credentials: "include",
        body: JSON.stringify({ newPassword }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed to set password");
      }

      // Resolve where to go after MFA step (or skip)
      const wsRes = await fetch("/api/auth/my-workspace", { credentials: "include" });
      const wsData = await wsRes.json().catch(() => ({}));
      setWorkspaceSlug(wsData.slug || null);

      setStep("mfa");
    } catch (err: any) {
      setPasswordError(err.message);
    } finally {
      setSubmittingPassword(false);
    }
  };

  const handleStartTotp = async () => {
    setStartingTotp(true);
    setTotpError("");
    try {
      const data = await (await apiRequest("POST", "/api/security/mfa/enroll/totp", {})).json();
      setTotpQr(data.qrDataUrl);
      setTotpSecret(data.secret);
    } catch (err: any) {
      setTotpError(err.message || "Could not start authenticator setup");
    } finally {
      setStartingTotp(false);
    }
  };

  const handleVerifyTotp = async () => {
    if (!totpToken || totpToken.length < 6) {
      setTotpError("Enter the 6-digit code from your authenticator app");
      return;
    }
    setVerifyingTotp(true);
    setTotpError("");
    try {
      await (await apiRequest("POST", "/api/security/mfa/enroll/totp/verify", { token: totpToken })).json();
      toast({ title: "Authenticator app activated" });
      redirectAfterFirstLogin();
    } catch (err: any) {
      setTotpError(err.message || "Verification failed");
    } finally {
      setVerifyingTotp(false);
    }
  };

  const redirectAfterFirstLogin = () => {
    if (workspaceSlug) {
      window.location.href = `/w/${workspaceSlug}/`;
    } else {
      window.location.href = "/select-workspace";
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900 p-4">
      <div className="w-full max-w-md space-y-4">
        {/* Step indicator */}
        <div className="flex items-center justify-center gap-3 mb-2">
          <div className={`flex items-center gap-1.5 text-sm font-medium ${step === "password" ? "text-primary" : "text-muted-foreground"}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 ${step === "password" ? "border-primary text-primary" : "border-muted-foreground bg-muted text-muted-foreground"}`}>
              {step === "mfa" ? <CheckCircle2 className="w-4 h-4" /> : "1"}
            </div>
            Set Password
          </div>
          <div className="w-8 h-px bg-border" />
          <div className={`flex items-center gap-1.5 text-sm font-medium ${step === "mfa" ? "text-primary" : "text-muted-foreground"}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 ${step === "mfa" ? "border-primary text-primary" : "border-muted-foreground"}`}>
              2
            </div>
            Secure Account
          </div>
        </div>

        {step === "password" && (
          <Card>
            <CardHeader className="text-center pb-4">
              <div className="flex justify-center mb-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <KeyRound className="w-6 h-6 text-primary" />
                </div>
              </div>
              <CardTitle className="text-xl">Set Your Password</CardTitle>
              <CardDescription>
                Your account was created by an administrator. Choose a secure password to continue.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                {passwordError && (
                  <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded text-sm">
                    {passwordError}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-1">New Password</label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setPasswordError(""); }}
                    placeholder="Min. 8 characters"
                    required
                    autoFocus
                    data-testid="input-new-password"
                  />
                  <PasswordStrengthBar password={newPassword} />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Confirm Password</label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setPasswordError(""); }}
                    placeholder="Repeat your new password"
                    required
                    data-testid="input-confirm-password"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={submittingPassword}
                  data-testid="button-set-password"
                >
                  {submittingPassword ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                  ) : (
                    "Continue"
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {step === "mfa" && (
          <Card>
            <CardHeader className="text-center pb-4">
              <div className="flex justify-center mb-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <ShieldCheck className="w-6 h-6 text-primary" />
                </div>
              </div>
              <CardTitle className="text-xl">Secure Your Account</CardTitle>
              <CardDescription>
                Add two-factor authentication for extra protection. You can skip this and set it up later in account settings.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {totpError && (
                <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded text-sm">
                  {totpError}
                </div>
              )}

              {!totpQr ? (
                <div className="space-y-3">
                  <Button
                    onClick={handleStartTotp}
                    disabled={startingTotp}
                    className="w-full"
                    data-testid="button-setup-totp"
                  >
                    {startingTotp ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting setup…</>
                    ) : (
                      <><Smartphone className="w-4 h-4 mr-2" /> Set Up Authenticator App</>
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full text-muted-foreground"
                    onClick={redirectAfterFirstLogin}
                    data-testid="button-skip-mfa"
                  >
                    <SkipForward className="w-4 h-4 mr-2" />
                    Skip for now
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground text-center">
                    Scan this QR code with your authenticator app (e.g. Google Authenticator, Authy).
                  </p>
                  <div className="flex justify-center">
                    <img
                      src={totpQr}
                      alt="TOTP QR Code"
                      className="w-44 h-44 border rounded"
                      data-testid="img-totp-qr"
                    />
                  </div>
                  {totpSecret && (
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground mb-1">Or enter this code manually:</p>
                      <Badge variant="secondary" className="font-mono text-xs tracking-widest">
                        {totpSecret}
                      </Badge>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium mb-1">Verification Code</label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={totpToken}
                      onChange={(e) => { setTotpToken(e.target.value.replace(/\D/g, "")); setTotpError(""); }}
                      placeholder="6-digit code"
                      autoFocus
                      data-testid="input-totp-token"
                    />
                  </div>
                  <Button
                    onClick={handleVerifyTotp}
                    disabled={verifyingTotp}
                    className="w-full"
                    data-testid="button-verify-totp"
                  >
                    {verifyingTotp ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying…</>
                    ) : (
                      "Activate Authenticator"
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full text-muted-foreground"
                    onClick={redirectAfterFirstLogin}
                    data-testid="button-skip-mfa-after-scan"
                  >
                    <SkipForward className="w-4 h-4 mr-2" />
                    Skip for now
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
